import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { CheckCircle2, Calendar, CreditCard, AlertCircle, ArrowLeft, Coins, Tag, X, HelpCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useModuleAccess } from '../../hooks/useModuleAccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = location.state?.plan;
  
  // Upgrade-specific data from navigation state
  const isUpgrade = location.state?.isUpgrade || false;
  const currentPlanName = location.state?.currentPlanName || null;
  const daysRemaining = location.state?.daysRemaining || 0;
  const initialRebateAmount = location.state?.rebateAmount || 0;

  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [coinsToRedeem, setCoinsToRedeem] = useState(0);
  const [userCoins, setUserCoins] = useState(0);
  const [coinValue, setCoinValue] = useState(10);
  // Redemption caps (from /user/coin-value). 0 = no cap.
  const [coinLimits, setCoinLimits] = useState({
    max_coins_per_order: 0,
    max_redemption_pct_of_order: 0,
    max_pct_of_balance_per_order: 0,
    max_redemption_per_month: 0,
  });
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [rebateAmount, setRebateAmount] = useState(initialRebateAmount);

  // Check if coin rewards module is active
  const { isFeatureActive } = useModuleAccess();
  const coinRewardsActive = isFeatureActive('coin_rewards');

  const fetchUserCoins = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUserCoins(response.data.coins_balance || 0);
    } catch (error) {
      console.error('Failed to fetch user coins:', error);
    }
  };

  const fetchCoinValue = async () => {
    try {
      const response = await axios.get(`${API}/user/coin-value`);
      setCoinValue(response.data.coin_value_inr || 10);
      setCoinLimits({
        max_coins_per_order: response.data.max_coins_per_order || 0,
        max_redemption_pct_of_order: response.data.max_redemption_pct_of_order || 0,
        max_pct_of_balance_per_order: response.data.max_pct_of_balance_per_order || 0,
        max_redemption_per_month: response.data.max_redemption_per_month || 0,
      });
    } catch (error) {
      console.error('Failed to fetch coin value:', error);
    }
  };

  useEffect(() => {
    // Redirect if no plan selected
    if (!plan) {
      toast.error('Please select a plan first');
      navigate('/plans');
    } else {
      // Fetch coin data if coin rewards is active
      if (coinRewardsActive) {
        fetchUserCoins();
        fetchCoinValue();
      }
    }
  }, [plan, navigate, coinRewardsActive]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      // Calculate the price to apply coupon on (after rebate for upgrades)
      const priceForCoupon = isUpgrade ? (plan.price - rebateAmount) : plan.price;
      
      // Validate coupon with backend
      const response = await axios.post(`${API}/user/validate-coupon`, {
        coupon_code: couponCode.toUpperCase(),
        plan_price: priceForCoupon
      });

      if (response.data.valid) {
        setAppliedCoupon({
          code: couponCode.toUpperCase(),
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value,
          discount_amount: response.data.discount_amount
        });
        toast.success(`Coupon applied! You save ${formatCurrency(response.data.discount_amount)}`);
      } else {
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon code');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const calculateTotal = () => {
    let subtotal = plan.price;
    let rebateDiscount = isUpgrade ? rebateAmount : 0;
    let couponDiscount = appliedCoupon?.discount_amount || 0;
    let coinDiscount = coinsToRedeem * coinValue;
    
    // Apply discounts in order: rebate first (for upgrades), then coupon, then coins
    let total = subtotal - rebateDiscount - couponDiscount - coinDiscount;
    
    // Ensure total doesn't go below 1
    total = Math.max(1, total);
    
    return {
      subtotal,
      rebateDiscount,
      couponDiscount,
      coinDiscount,
      total
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handlePayNow = async () => {
    setProcessing(true);
    setPaymentError(null);

    try {
      // Create order with unified endpoint (handles both purchase and upgrade)
      const orderResponse = await axios.post(`${API}/user/payment/create-order`, {
        plan_id: plan.id,
        coupon_code: appliedCoupon?.code || null,
        redeem_coins: coinsToRedeem,
        is_upgrade: isUpgrade
      });
      
      const orderData = orderResponse.data;

      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount * 100,
          currency: orderData.currency,
          name: 'MARQ by HelmTorq',
          description: isUpgrade ? `Upgrade to ${orderData.plan_name}` : orderData.plan_name,
          order_id: orderData.order_id,
          prefill: {
            name: orderData.user_name,
            email: orderData.user_email
          },
          theme: {
            color: '#3b82f6'
          },
          handler: async function (response) {
            // Payment successful - verify with unified endpoint, then hand off
            // to the /payment/success page which shows a proper confirmation
            // (plan/expiry/receipt) and busts the subscription-access cache.
            try {
              const verifyResponse = await axios.post(`${API}/payment/verify-and-activate`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });

              if (verifyResponse.data.success) {
                navigate(`/payment/success?order_id=${encodeURIComponent(response.razorpay_order_id)}`);
              } else {
                navigate(`/payment/failed?order_id=${encodeURIComponent(response.razorpay_order_id)}&reason=${encodeURIComponent('Verification did not succeed')}`);
              }
            } catch (error) {
              console.error('Payment verification error:', error);
              const reason = error.response?.data?.detail || 'Payment verification failed. Please contact support.';
              navigate(`/payment/failed?order_id=${encodeURIComponent(response.razorpay_order_id)}&reason=${encodeURIComponent(reason)}`);
            }
          },
          modal: {
            ondismiss: async function () {
              // User closed the modal without completing payment - mark as cancelled
              try {
                await axios.post(`${API}/user/payment/cancel-order`, {
                  order_id: orderData.order_id
                });
              } catch (err) {
                console.error('Failed to mark order as cancelled:', err);
              }
              setProcessing(false);
              toast.info('Payment cancelled');
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        
        // Handle payment failure
        razorpay.on('payment.failed', async function (response) {
          const orderIdForRedirect = orderData.order_id;
          const reason = response.error?.description || 'Payment failed. Please try again.';
          const code = response.error?.code || '';
          try {
            await axios.post(`${API}/user/payment/mark-failed`, {
              order_id: orderData.order_id,
              error_message: reason,
              error_code: code
            });
          } catch (err) {
            console.error('Failed to mark order as failed:', err);
          }
          setProcessing(false);
          navigate(
            `/payment/failed?order_id=${encodeURIComponent(orderIdForRedirect)}`
            + `&reason=${encodeURIComponent(reason)}`
            + (code ? `&code=${encodeURIComponent(code)}` : '')
            + (plan?.id ? `&plan_id=${encodeURIComponent(plan.id)}` : '')
          );
        });
        
        razorpay.open();
        setProcessing(false);
      };

      document.body.appendChild(script);
    } catch (error) {
      // Server sends structured errors for coin caps: { detail: { code, message } }
      // Fall back to plain string detail (older endpoints) or a generic line.
      const detail = error.response?.data?.detail;
      let msg = 'Failed to initiate payment';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object' && detail.message) msg = detail.message;
      toast.error(msg);
      setProcessing(false);
    }
  };

  if (!plan) {
    return null;
  }

  const totals = calculateTotal();

  // Compute the max coins the user can redeem right now, considering:
  //   1) their balance
  //   2) the amount left to pay (must leave ≥ ₹1 real money)
  //   3) per-order absolute cap (admin-configurable)
  //   4) % of order value cap (admin-configurable)
  //   5) % of balance cap (admin-configurable)
  // Returns 0 (never negative). The tightest cap wins.
  const computeMaxCoins = () => {
    const orderRemaining = Math.max(0, totals.subtotal - totals.rebateDiscount - totals.couponDiscount - 1);
    const maxFromAmount = Math.floor(orderRemaining / coinValue);
    let cap = Math.min(userCoins, maxFromAmount);
    if (coinLimits.max_coins_per_order > 0) cap = Math.min(cap, coinLimits.max_coins_per_order);
    const orderGross = totals.subtotal - totals.rebateDiscount - totals.couponDiscount;
    if (coinLimits.max_redemption_pct_of_order > 0 && coinLimits.max_redemption_pct_of_order < 100 && orderGross > 0) {
      const maxRupees = Math.floor((orderGross * coinLimits.max_redemption_pct_of_order) / 100);
      cap = Math.min(cap, Math.floor(maxRupees / coinValue));
    }
    if (coinLimits.max_pct_of_balance_per_order > 0 && coinLimits.max_pct_of_balance_per_order < 100) {
      cap = Math.min(cap, Math.floor((userCoins * coinLimits.max_pct_of_balance_per_order) / 100));
    }
    return Math.max(0, cap);
  };

  // Return a friendly one-liner telling the user which cap is the binding one
  // (i.e. why they can't redeem more). Empty string if the amount-remaining
  // is the only binder (that's already obvious from the UI).
  const describeCoinCap = () => {
    const capValue = computeMaxCoins();
    if (userCoins <= capValue) return '';
    if (coinLimits.max_coins_per_order > 0 && capValue === coinLimits.max_coins_per_order) {
      return `You can redeem a maximum of ${coinLimits.max_coins_per_order} coins per order.`;
    }
    if (coinLimits.max_pct_of_balance_per_order > 0 && coinLimits.max_pct_of_balance_per_order < 100) {
      const balCap = Math.floor((userCoins * coinLimits.max_pct_of_balance_per_order) / 100);
      if (capValue === balCap) {
        return `You can spend up to ${coinLimits.max_pct_of_balance_per_order}% of your ${userCoins}-coin balance in one order (max ${balCap} coins).`;
      }
    }
    if (coinLimits.max_redemption_pct_of_order > 0 && coinLimits.max_redemption_pct_of_order < 100) {
      return `Coins can cover up to ${coinLimits.max_redemption_pct_of_order}% of this order (max ${capValue} coins here).`;
    }
    return '';
  };

  return (
    <Layout>
      <div className="container mx-auto p-6 max-w-3xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(isUpgrade ? -1 : '/plans')}
          className="mb-4"
          data-testid="checkout-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isUpgrade ? 'Back' : 'Back to Plans'}
        </Button>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold mb-2">
            {isUpgrade ? 'Upgrade Your Plan' : 'Checkout'}
          </h1>
          <p className="text-gray-600">
            {isUpgrade 
              ? 'Review your upgrade and complete payment' 
              : 'Review your order and complete payment'
            }
          </p>
        </div>

        {/* Upgrade Summary Card (Upgrade only) */}
        {isUpgrade && (
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" data-testid="upgrade-summary-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                <h3 className="font-semibold text-blue-900">Plan Upgrade</h3>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex-1 p-3 bg-white rounded-lg border">
                  <p className="text-gray-500 text-xs mb-1">Current Plan</p>
                  <p className="font-medium">{currentPlanName || 'Current Plan'}</p>
                  {daysRemaining > 0 && (
                    <p className="text-xs text-gray-500">{daysRemaining} days remaining</p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <div className="flex-1 p-3 bg-blue-600 text-white rounded-lg">
                  <p className="text-blue-200 text-xs mb-1">New Plan</p>
                  <p className="font-medium">{plan.name}</p>
                  <p className="text-xs text-blue-200">{plan.duration_days} days validity</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plan Details Card */}
        <Card className="mb-6" data-testid="plan-details-card">
          <CardHeader>
            <CardTitle>Plan Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Plan Name */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{plan.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{plan.description}</p>
                </div>
                <Badge className="bg-blue-600">{plan.duration_days} Days</Badge>
              </div>

              <Separator />

              {/* Features */}
              <div>
                <p className="font-medium mb-3">What&apos;s Included:</p>
                <div className="space-y-2">
                  {plan.features && Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Validity */}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {isUpgrade 
                    ? `New validity: ${plan.duration_days} days from today`
                    : `Valid for ${plan.duration_days} days from activation`
                  }
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Summary Card */}
        <Card className="mb-6" data-testid="price-summary-card">
          <CardHeader>
            <CardTitle>Price Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Plan Price */}
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{isUpgrade ? 'New Plan Price' : 'Plan Price'}</span>
                <span className="font-semibold">{formatCurrency(plan.price)}</span>
              </div>

              {/* Pro-rata Rebate (for upgrades) */}
              {isUpgrade && rebateAmount > 0 && (
                <div className="flex justify-between items-center text-green-700 bg-green-50 p-2 rounded-lg">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Credit from current plan
                    <span className="text-xs text-green-600">({daysRemaining} unused days)</span>
                  </span>
                  <span className="font-semibold">-{formatCurrency(rebateAmount)}</span>
                </div>
              )}

              <Separator />

              {/* Coupon Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Tag className="w-4 h-4" />
                  <span>Have a coupon code?</span>
                </div>
                
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="coupon-input"
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={applyingCoupon || !couponCode.trim()}
                      variant="outline"
                      data-testid="apply-coupon-button"
                    >
                      Apply
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">{appliedCoupon.code}</span>
                        <span className="text-xs text-green-600">
                          ({appliedCoupon.discount_type === 'percentage' 
                            ? `${appliedCoupon.discount_value}% off` 
                            : `₹${appliedCoupon.discount_value} off`})
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleRemoveCoupon}
                        className="text-red-600 hover:text-red-700"
                        data-testid="remove-coupon-button"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">Coupon Discount</span>
                      <span className="font-semibold text-green-700">-{formatCurrency(appliedCoupon.discount_amount)}</span>
                    </div>
                  </>
                )}
              </div>

              <Separator />

              {/* Coin Redemption Section - Show if coin rewards is active */}
              {coinRewardsActive && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Coins className="w-4 h-4 text-amber-600" />
                      <span>Redeem Reward Coins</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      Available: <strong className="text-amber-600">{userCoins}</strong> coins
                    </span>
                  </div>

                  {userCoins > 0 ? (
                    <div className="space-y-2">
                      {(() => { /* max caps computed once per render for use in the input + button */ }) || null}
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="0"
                          max={computeMaxCoins()}
                          value={coinsToRedeem}
                          onChange={(e) => {
                            setCoinsToRedeem(Math.min(Math.max(0, parseInt(e.target.value) || 0), computeMaxCoins()));
                          }}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="coins-input"
                        />
                        <span className="text-sm text-gray-600">coins</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCoinsToRedeem(computeMaxCoins())}
                          data-testid="use-max-coins-button"
                        >
                          Use Max
                        </Button>
                      </div>
                      {/* Show the actual binding cap so the user knows why they can't redeem more */}
                      {(() => {
                        const info = describeCoinCap();
                        return info ? (
                          <p className="text-xs text-gray-500 leading-snug">
                            {info}
                          </p>
                        ) : null;
                      })()}
                      
                      {coinsToRedeem > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">
                              {coinsToRedeem} coins × ₹{coinValue}
                            </span>
                            <span className="font-semibold text-amber-700">
                              -{formatCurrency(coinsToRedeem * coinValue)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm text-gray-600 text-center">
                        You don&apos;t have any coins yet. Earn coins by contributing questions!
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Separator />

              {/* Total */}
              <div className="flex justify-between items-center text-lg font-bold" data-testid="total-amount">
                <span>{isUpgrade ? 'Amount to Pay' : 'Total Amount'}</span>
                <span className="text-blue-600">{formatCurrency(totals.total)}</span>
              </div>

              {totals.total === 1 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700">
                    ℹ️ Minimum payment of ₹1 is required to maintain transaction records
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Info */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Secure Payment</p>
                <p>
                  Your payment is processed securely through Razorpay. 
                  {isUpgrade 
                    ? ' Your new plan will be activated immediately upon successful payment with fresh validity.'
                    : ' Your subscription will be activated immediately upon successful payment.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pay Now Button */}
        <Button
          size="lg"
          className="w-full"
          onClick={handlePayNow}
          disabled={processing}
          data-testid="checkout-pay-button"
        >
          {processing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5 mr-2" />
              {isUpgrade ? `Pay ${formatCurrency(totals.total)} & Upgrade` : `Pay ${formatCurrency(totals.total)} Now`}
            </>
          )}
        </Button>

        {/* Payment Error Display */}
        {paymentError && (
          <Card className="mt-4 bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <p className="font-medium mb-1">Payment Failed</p>
                  <p className="mb-2">{paymentError}</p>
                  <p className="text-xs text-red-700">
                    Please try again or use a different payment method. If the issue persists, contact our support team.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <div className="text-center mt-6 pt-4 border-t border-gray-200">
          <Link 
            to="/contact-us"
            className="text-sm text-gray-500 hover:text-[#0A7EA4] inline-flex items-center gap-1"
          >
            <HelpCircle size={14} />
            Having trouble with payment? Contact us
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;
